import { useCallback, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client/react';
import { withApollo } from 'hoc/withApollo';
import FormController, {
  ValidationRuleType,
} from 'components/controllers/FormController';
import {
  Grid, TextField, Typography,
} from '@mui/material';
import ClassicButton from 'components/buttons/ClassicButton';
import DoneIcon from '@mui/icons-material/Done';
import { useSessionState } from 'context/session/session';

const useStyles = makeStyles()((theme) => ({
  newsletter: {
    padding: '2em 0',
    textAlign: 'center',
    [theme.breakpoints.down('lg')]: {
      padding: '1em 0',
    },
  },
  align: {
    textAlign: 'center',
  },
  newsletterLegal: {
    textAlign: 'justify',
    padding: '1em',
    fontSize: '0.9em',
  },
  search: {
    position: 'relative',
    borderRadius: '9em',
    width: '35%',
    margin: '0 auto',
    [theme.breakpoints.down('lg')]: {
      width: '50%',
    },
    [theme.breakpoints.down('md')]: {
      width: '70%',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  searchIcon: {
    width: '56px',
    height: '52px',
    borderRadius: '30px',
    backgroundColor: '#2C367E',
    color: 'white',
    position: 'absolute',
    right: '2px',
    top: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  inputRoot: {
    width: '100%',
  },
  inputInput: {
    width: '100%',
    height: '36px',
    color: '#A3A3A3',
    fontStyle: 'italic',
    margin: '0',
    '& div': {
      borderRadius: '30px',
      [theme.breakpoints.down('md')]: {
        fontSize: '0.8rem',
      },
    },
    '& input': {
      padding: '18.5px 14px',
    },
    '& fieldset': {
      borderRadius: '30px',
    },
  },
  cardTitle: {
    align: 'center',
    fontSize: '2.2em',
    color: '#2C367E',
    letterSpacing: '2px',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.3em',
    },
  },
  buttonGrid: {
    color: 'white',
    backgroundColor: '#2C367E',
    border: 'none',
    fontSize: '1.2em',
    fontWeight: 'bold',
    borderRadius: '1.5em',
    padding: '0.2em 3em 0.2em 3em',
    minHeight: '2.5em',
    margin: '1.5em 0 1.5em 0',
    backgroundImage: "url('/arrow.svg')",
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: '5px',
    backgroundPositionY: 'center',
    backgroundSize: '14%',
    '&:hover': {
      cursor: 'pointer',
      color: '#2C367E',
      backgroundColor: 'white',
      border: '2px solid #2C367E',
      backgroundImage: "url('/arrow-hover.svg')",
      backgroundPositionX: '5px',
      backgroundPositionY: 'center',
      backgroundSize: '14%',
    },
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.2em',
      minHeight: '2.5em',
    },
  },
}));

const ADD_NEWSLETTER_EMAIL = gql`
  mutation createNewsletterEmail($email: String!) {
    createNewsletterEmail(email: $email) {
      id
    }
  }
`;

const ADD_NEWSLETTER_USER = gql`
  mutation createNewsletterUser($userId: Int!) {
    createNewsletterUser(userId: $userId) {
      id
    }
  }
`;

const validationRules: import('components/controllers/FormController').ValidationRules = {
  email: {
    rule: ValidationRuleType.email,
  },
};

interface NewsletterProps {
  id?: string;
}

const Newsletter = (prop: NewsletterProps) => {
  const Form = (props: any) => {
    const { classes: styles } = useStyles();
    const user = useSessionState();
    const [subscribed, setSubscribed] = useState(false);
    const { formChangeHandler, formValues, validationResult } = props;

    const [
      newsletterEmail,
      { data: dataVisitor, error: errorVisitor },
    ] = useMutation(ADD_NEWSLETTER_EMAIL);
    const [newsletterUser, { data: dataUser, error: errorUser }] = useMutation(
      ADD_NEWSLETTER_USER,
    );

    const subscription = useCallback(() => {
      if (!user) {
        newsletterEmail({
          variables: {
            email: formValues.email,
          },
        });
      } else {
        newsletterUser({
          variables: {
            userId: parseInt(String(user.id)),
          },
        });
      }
      if (!errorVisitor && !errorUser) {
        setSubscribed(true);
      }
    }, [
      newsletterEmail,
      newsletterUser,
      formValues,
      setSubscribed,
      errorVisitor,
      errorUser,
      user,
    ]);

    return (
      <Grid id={prop.id} container direction="column" alignItems="center" justifyContent="space-around" className={styles.newsletter}>
        <Typography variant="h2" className={[styles.cardTitle, styles.align].join(' ')}>
          POUR NE RIEN RATER DE
          {' '}
          <i>OUAAA!</i>
          <br />
          INSCRIVEZ-VOUS À NOTRE NEWSLETTER
        </Typography>
        <div className={styles.newsletterLegal}>
          Les données personnelles relatives à la création du compte (nom/prénom, structure si
          professionnel, numéro de téléphone, email) sont destinées à permettre l'identification sur le site
          et l'accès à son compte et aux fonctionnalités correspondantes. Les données sont conservées
          tant que le compte est actif. Une fois par an, le responsable de traitement peut effectuer une
          campagne de vérification d'adresse mails pour supprimer les comptes inactifs après vérification
          par contact téléphonique
          <br />
          Les fiches acteurs reliées à des comptes inactifs seront mises à jour avec la mention suivante :
          coordonnées obsolètes à telle date. Pour toute question, vous pouvez joindre l'administrateur du
          site à contact@ouaaa-transition.fr et exercer vos droits d'accès, de rectification ou de suppression
          aurprès du DPD à dpd@ouaaa-transition.fr
        </div>
        {!subscribed && !user && (
          <div className={styles.search}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="email"
              autoComplete="current-email"
              placeholder="J'inscris mon email pour recevoir la newsletter"
              defaultValue=""
              className={styles.inputInput}
              value={formValues?.email}
              onChange={formChangeHandler}
            />
            <ClassicButton
              variant="contained"
              className={styles.searchIcon}
              onClick={subscription}
              disabled={!validationResult?.global}
            >
              <DoneIcon />
            </ClassicButton>
          </div>
        )}
        {!subscribed && user && (
          <button
            className={styles.buttonGrid}
            onClick={subscription}
          >
            M'abonner à la newsletter
          </button>
        )}
        {subscribed && (
          <Typography variant="h5">
            Inscription à la newsletter bien effectuée.
          </Typography>
        )}
      </Grid>
    );
  };

  return <FormController render={Form} validationRules={validationRules} />;
};

export default withApollo()(Newsletter);
